import { ChangeDetectorRef, Component, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray } from '@angular/forms';
import { ParticipantService } from '../../../services/participant.service';
import { ProjectService } from '../../../services/project.service';
import { CurrencyService } from '../../../services/currency.service';
import { ExchangeRateService } from '../../../services/exchange-rate.service';
import { forkJoin, merge, Subscription } from 'rxjs';
import { LoadingService } from '../../../services/loading.service';
import { EnrollmentService } from '../../../services/enrollment.service';
import { ToastService } from '../../../services/toast.service';
import { Router } from '@angular/router';
import { SignalRServiceService } from '../../../services/signal-rservice.service';
import { CompanyService } from '../../../services/company.service';
import { CorporateEnrollmentService, CoverageModality, CoverageType, CorporateEnrollmentRequest, CorporateEnrollmentParticipant, CompanyPaymentPlan, MasterPaymentPlan } from '../../../services/corporate-enrollment.service';
import { BaseFormComponent } from '../../shared/base-form/base-form.component';
import { ConfirmationService } from '../../../services/confirmation.service';
import { ConfirmationModalComponent } from '../../shared/confirmation-modal/confirmation-modal.component';


@Component({
  selector: 'app-new-enrollment',
  templateUrl: './new-enrollment.component.html',
  styleUrls: ['./new-enrollment.component.scss']
})
export class NewEnrollmentComponent extends BaseFormComponent implements OnInit, OnDestroy, OnChanges {
  enrollmentForm!: FormGroup;
  projects: any;
  participants: any;
  isBlocked = false;
  step = 1;
  totalAssignedAmount = 0;
  exceedsTotalAmount = false;

  // Propiedades para manejo de tipo de inscripción
  enrollmentTypes = [
    {
      value: 'national',
      label: 'Individual Nacional',
      description: 'Para estudiantes colombianos o residentes en Colombia. Pagos en pesos colombianos (COP).',
      icon: '🇨🇴'
    },
    {
      value: 'business',
      label: 'Corporativa Nacional',
      description: 'Inscripciones de colaboradores con coberturas pagadas por la empresa. Pagos en COP.',
      icon: '🏢'
    },
    {
      value: 'international',
      label: 'Individual Internacional',
      description: 'Para estudiantes extranjeros. Requiere conversión de moneda y manejo de tasas de cambio.',
      icon: '🌍'
    },
    {
      value: 'business-international',
      label: 'Corporativa Internacional',
      description: 'Inscripciones corporativas con moneda extranjera. Conversión automática de tasas de cambio.',
      icon: '🌐'
    }
  ];

  selectedEnrollmentType: string = '';

  // Propiedades para manejo de monedas internacionales
  selectedProjectForCurrency: any = null;
  isInternationalProject: boolean = false;
  projectCurrency: any = null;
  currentExchangeRate: number = 0;
  lastExchangeRateUpdate: Date | null = null;

  // 🆕 Empresarial - Flujo mejorado con modalidades A/B
  companies: any[] = [];
  selectedCompanyId: number | null = null;
  selectedProjectId: number | null = null;
  businessParticipants: Array<{ participantId: number | null, coverageType: string, coverageValue: number | null }> = [];
  businessStep: number = 1;
  businessObservations: string = '';

  // 🆕 Monto editable de inscripción
  businessInscriptionAmount: number = 0;

  // 🌍 Propiedades para inscripciones corporativas internacionales
  businessIsInternational: boolean = false;
  businessCurrencyId: number | null = null;
  businessCurrencyCode: string = 'COP';
  businessExchangeRate: number = 0;
  businessLastExchangeRateUpdate: Date | null = null;

  // 🟢 Nuevas propiedades para modalidades de cobertura
  selectedCoverageModality: CoverageModality = CoverageModality.Partial;
  CoverageModality = CoverageModality;  // Para usar en template
  CoverageType = CoverageType;          // Para usar en template

  // Cálculos consolidados
  totalEnrollmentAmount: number = 0;
  totalCompanyAmount: number = 0;
  totalParticipantAmount: number = 0;

  // Planes de pago específicos
  companyPaymentPlans: CompanyPaymentPlan[] = [];
  masterPaymentPlans: MasterPaymentPlan[] = [];

  // Opciones de estado para la inscripción
  statusOptions = [
    { label: 'Pendiente', value: 0 },
    { label: 'Activa', value: 1 }
  ];

  // 🏢 Estado inicial y cobro automático para el flujo CORPORATIVO.
  // El wizard empresarial usa ngModel standalone (no reactive forms),
  // por eso estos campos viven en propiedades del componente y no en enrollmentForm.
  businessStatus: number = 0; // 0 = Pendiente (default), 1 = Activa
  businessAutoGeneratePaymentLinks: boolean = true;


  // ❌ REMOVIDO: whoPaysOptions (obsoleto tras separación individual/corporativo)

  private formSubscription!: Subscription;
  private lockSubscription!: Subscription;

  constructor(
    private fb: FormBuilder,
    private _projectService: ProjectService,
    private _participantService: ParticipantService,
    private _currencyService: CurrencyService,
    private _exchangeRateService: ExchangeRateService,
    private _loadingService: LoadingService,
    private _enrollmentService: EnrollmentService,
    private toastService: ToastService,
    router: Router,
    private _signalRService: SignalRServiceService,
    private cdr: ChangeDetectorRef,
    private _companyService: CompanyService,
    private _corporateEnrollmentService: CorporateEnrollmentService,
    confirmationService: ConfirmationService
  ) {
    super(confirmationService, router);
  }

  // === MÉTODOS REQUERIDOS POR BaseFormComponent ===

  protected override initializeForm(): void {
    this.enrollmentForm = this.fb.group({
      enrollmentType: ['', Validators.required],
      projectId: ['', Validators.required],
      participantId: ['', Validators.required],
      dateEnrolled: ['', Validators.required],
      totalAmount: [0, [Validators.required, Validators.min(1)]],
      inscriptionAmount: [0, [Validators.required, Validators.min(1)]],
      copEquivalentAmount: [0],
      currencyId: [1, Validators.required],
      exchangeRate: [1, Validators.required],
      status: [0, Validators.required], // Default: Pending (0)
      autoGeneratePaymentLinks: [true], // 🆕 Cobro automático vía email
      observations: [''],
      paymentPlans: this.fb.array([])
    });
    this.setupAdditionalConfigurations();
  }

  protected override getMainForm(): FormGroup {
    return this.enrollmentForm;
  }

  public override getFormName(): string {
    const typeLabels = {
      'national': 'inscripción individual nacional',
      'international': 'inscripción individual internacional',
      'business': 'inscripción corporativa nacional',
      'business-international': 'inscripción corporativa internacional'
    };
    return typeLabels[this.selectedEnrollmentType as keyof typeof typeLabels] || 'nueva inscripción';
  }

  public override getCriticalData(): string[] {
    const data: string[] = [];

    if (this.selectedEnrollmentType === 'national' || this.selectedEnrollmentType === 'international') {
      // Información del proyecto
      const project = this.selectedProject;
      if (project) {
        data.push(`Proyecto: ${project.projectId} - ${project.name}`);
      }

      // Información del participante
      const participantId = this.enrollmentForm.get('participantId')?.value;
      const participant = this.participants?.find((p: any) => p.id === participantId);
      if (participant) {
        data.push(`Participante: ${participant.firstName} ${participant.lastName}`);
      }

      // Total asignado
      data.push(`Total asignado: ${this.formatCurrency(this.totalAssignedAmount)}`);

    } else if (this.selectedEnrollmentType === 'business' || this.selectedEnrollmentType === 'business-international') {
      // Información de la empresa
      if (this.getSelectedCompanyName()) {
        data.push(`Empresa: ${this.getSelectedCompanyName()}`);
      }

      // Información del proyecto
      const project = this.selectedProject;
      if (project) {
        data.push(`Proyecto: ${project.projectId} - ${project.name}`);
      }

      // Modalidad de cobertura
      const modality = this.selectedCoverageModality === CoverageModality.Total ? 'Total' : 'Parcial';
      data.push(`Modalidad: ${modality}`);

      // 🌍 Información de moneda para inscripciones internacionales
      if (this.businessIsInternational) {
        data.push(`Moneda: ${this.businessCurrencyCode}`);
        if (this.businessExchangeRate > 0) {
          data.push(`Tasa de cambio: ${this.formatBusinessExchangeRate()}`);
        }
      }

      // Participantes
      data.push(`Participantes: ${this.businessParticipants.length}`);

      // Totales
      const currencySymbol = this.getBusinessCurrencySymbol();
      data.push(`Total inscripción: ${currencySymbol}${this.formatCurrency(this.totalEnrollmentAmount)}`);
      data.push(`Total empresa: ${currencySymbol}${this.formatCurrency(this.totalCompanyAmount)}`);
      data.push(`Total participantes: ${currencySymbol}${this.formatCurrency(this.totalParticipantAmount)}`);
    }

    return data;
  }

  protected override async performFormSubmit(): Promise<void> {
    if (this.selectedEnrollmentType === 'national' || this.selectedEnrollmentType === 'international') {
      await this.submitIndividualEnrollment();
    } else if (this.selectedEnrollmentType === 'business' || this.selectedEnrollmentType === 'business-international') {
      await this.submitBusinessEnrollment();
    }
  }

  protected override navigateBack(): void {
    this.router.navigate(['/dashboard/enrollments']);
  }

  ngOnChanges(changes: SimpleChanges): void {

    console.log('Changes detected:', changes);
    if (changes['enrollmentForm']) {
      this.enrollmentForm = changes['enrollmentForm'].currentValue;
    }

  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.setupProjectSelectionListener();
    this.setupLockListeners();
    this.loadInitialData();
    this.validateAssignedAmount();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.formSubscription?.unsubscribe();
    this.lockSubscription?.unsubscribe();
    this.unlockCurrentEnrollment();
  }

  isCurrentStepValid() {
    // Paso 0: Verificar que se haya seleccionado el tipo de inscripción
    if (!this.selectedEnrollmentType) {
      return false;
    }

    if (this.step === 1) {
      return (
        this.enrollmentForm.get('enrollmentType')?.valid &&
        this.enrollmentForm.get('projectId')?.valid &&
        this.enrollmentForm.get('participantId')?.valid &&
        this.enrollmentForm.get('dateEnrolled')?.valid &&
        this.enrollmentForm.get('totalAmount')?.valid &&
        this.enrollmentForm.get('inscriptionAmount')?.valid &&
        this.enrollmentForm.get('status')?.valid // 🆕 Estado Inicial obligatorio
      );
    }

    if (this.step === 2) {
      const plans = this.enrollmentForm.get('paymentPlans') as FormArray;
      const plansValid = plans.length > 0 && plans.controls.every(plan => plan.valid);
      // 🔧 Verificar que la suma esté dentro de la tolerancia permitida
      return plansValid && this.isTotalAmountWithinTolerance();
    }

    return false;
  }

  /**
   * 🔧 Verifica si la suma de las cuotas está dentro de la tolerancia permitida
   * Permite pequeñas diferencias por redondeos (hasta 1 unidad o 0.1% del total)
   */
  isTotalAmountWithinTolerance(): boolean {
    const totalAmount = Number(this.enrollmentForm.get('totalAmount')?.value || 0);
    if (totalAmount <= 0) return false;

    const tolerance = Math.max(1, totalAmount * 0.001); // Máximo entre 1 unidad o 0.1% del total
    const difference = Math.abs(this.totalAssignedAmount - totalAmount);

    return difference <= tolerance;
  }

  /**
   * 🔧 Verifica si hay un error significativo en la distribución de cuotas
   * (fuera de la tolerancia permitida)
   */
  hasSignificantTotalError(): boolean {
    const totalAmount = Number(this.enrollmentForm.get('totalAmount')?.value || 0);
    if (totalAmount <= 0) return false;

    const tolerance = Math.max(1, totalAmount * 0.001);
    const difference = Math.abs(this.totalAssignedAmount - totalAmount);

    return difference > tolerance;
  }

  validateAssignedAmount(): void {
    const totalAmount = Number(this.enrollmentForm.get('totalAmount')?.value || 0);
    const sum = this.paymentPlans.controls.reduce((acc, group) => {
      const value = Number(group.get('amountToPay')?.value || 0);
      return acc + value;
    }, 0);

    this.totalAssignedAmount = sum;
    this.exceedsTotalAmount = sum > totalAmount;

    // 🔧 Tolerancia para manejar redondeos: permite hasta 1 unidad monetaria o 0.1% del total (lo que sea mayor)
    const tolerance = Math.max(1, totalAmount * 0.001); // Máximo entre 1 unidad o 0.1% del total
    const difference = Math.abs(sum - totalAmount);
    const isInvalidTotal = difference > tolerance;

    if (isInvalidTotal && this.step === 2) {
      this.enrollmentForm.setErrors({ totalMismatch: true });
    } else {
      this.enrollmentForm.setErrors(null);
    }
  }


  logInvalidControls() {
    console.log('Validando controles...', this.enrollmentForm.controls);
    const invalid: { control: string, errors: any }[] = [];

    const controls = this.enrollmentForm.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid.push({ control: name, errors: controls[name].errors });
      }
    }

    // También revisamos el FormArray
    const plans = this.enrollmentForm.get('paymentPlans') as FormArray;
    plans.controls.forEach((group, index) => {
      const fg = group as FormGroup;
      Object.keys(fg.controls).forEach(key => {
        const control = fg.get(key);
        if (control && control.invalid) {
          invalid.push({
            control: `paymentPlans[${index}].${key}`,
            errors: control.errors
          });
        }
      });
    });

    console.log('❌ Controles inválidos:', invalid);
  }

  // 🆕 Cache and loading state for participants
  participantCache = new Map<number, any>();
  isSearchingParticipants = false;

  private updateParticipantsList(participantsData: any[]): void {
    // 1. Update cache with new data
    participantsData.forEach(data => {
      this.participantCache.set(data.id, {
        label: `${data.typeDocument} ${data.documentId} ${data.firstName} ${data.lastName}`,
        value: data.id
      });
    });

    // 2. Map new data
    const newList = participantsData.map(data => ({
      label: `${data.typeDocument} ${data.documentId} ${data.firstName} ${data.lastName}`,
      value: data.id
    }));

    // 3. Ensure already selected participants are included in the list
    const currentIds = new Set(newList.map(item => item.value));

    // Check business participants
    this.businessParticipants.forEach(bp => {
      if (bp.participantId && !currentIds.has(bp.participantId)) {
        const cachedItem = this.participantCache.get(bp.participantId);
        if (cachedItem) {
          newList.push(cachedItem);
          currentIds.add(bp.participantId);
        }
      }
    });

    // Check individual participant
    const singleParticipantId = this.enrollmentForm?.get('participantId')?.value;
    if (singleParticipantId && !currentIds.has(singleParticipantId)) {
      const cachedItem = this.participantCache.get(singleParticipantId);
      if (cachedItem) {
        newList.push(cachedItem);
        currentIds.add(singleParticipantId);
      }
    }

    this.participants = newList;
  }

  // 🔁 Configuraciones adicionales después de inicializar formulario
  private setupAdditionalConfigurations(): void {
    // Configurar listener para el tipo de inscripción
    this.setupEnrollmentTypeListener();

    // Configurar watchers para cambios de moneda
    this.setupCurrencyWatchers();

    // Cargar al menos un plan de pago por defecto
    this.addPaymentPlan();
  }

  // 🔁 Cargar datos de participantes y proyectos
  loadInitialData(): void {
    this._loadingService.show();
    forkJoin({
      participants: this._participantService.getAll(),
      projects: this._projectService.getLookup(),   // 🆕 antes: getAll()
      companies: this._companyService.getAll()
    }).subscribe({
      next: (results) => {
        // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
        const participantsData = Array.isArray(results.participants.data) ? results.participants.data : (results.participants.data?.data || []);
        const projectsData = Array.isArray(results.projects.data) ? results.projects.data : (results.projects.data?.data || []);
        const companiesData = Array.isArray(results.companies.data) ? results.companies.data : (results.companies.data?.data || []);

        this.updateParticipantsList(participantsData);
        this.projects = projectsData.map((data: any) => ({ label: `${data.projectId} ${data.name}`, value: data.id, data }));
        this.companies = companiesData.map((c: any) => ({ label: `${c.name} (${c.documentId})`, value: c.id, data: c }));
        this._loadingService.hide();
      },
      error: (err) => {
        console.error('Error loading data:', err);
        this._loadingService.hide();
      }
    });
  }

  // 🔍 Búsqueda server-side para participantes
  onSearchParticipants(event: any): void {
    const query = event.filter;
    // Si la consulta está vacía o tiene menos de 3 caracteres, volvemos a cargar todo
    if (!query || query.length < 3) {
      this.isSearchingParticipants = true;
      this._participantService.getAll().subscribe({
        next: (results) => {
          const participantsData = Array.isArray(results.data) ? results.data : (results.data?.data || []);
          this.updateParticipantsList(participantsData);
          this.isSearchingParticipants = false;
        },
        error: (err) => {
          console.error('Error loading participants:', err);
          this.isSearchingParticipants = false;
        }
      });
      return;
    }

    this.isSearchingParticipants = true;
    this._participantService.getAllFiltered(query).subscribe({
      next: (results) => {
        const participantsData = Array.isArray(results.data) ? results.data : (results.data?.data || []);
        this.updateParticipantsList(participantsData);
        this.isSearchingParticipants = false;
      },
      error: (err) => {
        console.error('Error searching participants:', err);
        this.isSearchingParticipants = false;
      }
    });
  }

  // 🔍 Búsqueda server-side para empresas
  onSearchCompanies(event: any): void {
    const query = event.filter;
    if (!query || query.length < 3) {
      // Si hay menos de 3 caracteres, recargar datos iniciales
      this._companyService.getAll().subscribe({
        next: (results) => {
          const companiesData = Array.isArray(results.data) ? results.data : (results.data?.data || []);
          this.companies = companiesData.map((c: any) => ({
            label: `${c.name} (${c.documentId})`,
            value: c.id,
            data: c
          }));
        },
        error: (err) => console.error('Error loading companies:', err)
      });
      return;
    }

    this._companyService.getAllFiltered(query).subscribe({
      next: (results) => {
        const companiesData = Array.isArray(results.data) ? results.data : (results.data?.data || []);
        this.companies = companiesData.map((c: any) => ({
          label: `${c.name} (${c.documentId})`,
          value: c.id,
          data: c
        }));
      },
      error: (err) => {
        console.error('Error searching companies:', err);
      }
    });
  }

  // 🔍 Búsqueda server-side para proyectos
  onSearchProjects(event: any): void {
    const query = event.filter;
    if (!query || query.length < 3) {
      // Si hay menos de 3 caracteres, recargar datos iniciales
      this._projectService.getAll().subscribe({
        next: (results) => {
          const projectsData = Array.isArray(results.data) ? results.data : (results.data?.data || []);
          this.projects = projectsData.map((data: any) => ({
            label: `${data.projectId} ${data.name}`,
            value: data.id,
            data
          }));
        },
        error: (err) => console.error('Error loading projects:', err)
      });
      return;
    }

    this._projectService.getAllFiltered(query).subscribe({
      next: (results) => {
        const projectsData = Array.isArray(results.data) ? results.data : (results.data?.data || []);
        this.projects = projectsData.map((data: any) => ({
          label: `${data.projectId} ${data.name}`,
          value: data.id,
          data
        }));
      },
      error: (err) => {
        console.error('Error searching projects:', err);
      }
    });
  }

  // Empresarial API hooks (llamará al servicio cuando esté disponible en FE)
  addBusinessParticipant(): void {
    this.businessParticipants.push({
      participantId: null,
      coverageType: this.selectedCoverageModality === CoverageModality.Total ? CoverageType.FULL : CoverageType.PERCENTAGE,
      coverageValue: this.selectedCoverageModality === CoverageModality.Total ? 100 : 50
    });
    this.calculateConsolidatedTotals();
  }

  async removeBusinessParticipant(index: number): Promise<void> {
    if (index < 0 || index >= this.businessParticipants.length) return;

    const participant = this.businessParticipants[index];
    const canRemove = await this.confirmDelete('participante empresarial', `el participante en posición ${index + 1}`);

    if (canRemove) {
      this.businessParticipants.splice(index, 1);
      this.calculateConsolidatedTotals();
    }
  }

  previewBusinessPlan(): void {
    this.toastService.showToast('success', 'Vista previa generada (prototipo).');
  }

  // Método movido al final de la clase - ver línea 972

  // 🧮 NUEVAS PROPIEDADES PARA CÁLCULO AUTOMÁTICO
  showCalculateModal = false;
  autoCalculateInstallments = 3; // Valor por defecto
  minInstallments = 1;

  // 🚨 MODAL DE ALERTA DE LINKS DE PAGO AUTOMÁTICO
  showPaymentLinkWarningModal = false;
  private pendingSubmitAfterWarning = false;
  maxInstallments = 100; // 🔓 Límite aumentado a 100 (virtualmente libre)

  // 🧮 MÉTODOS DE CÁLCULO AUTOMÁTICO DE CUOTAS
  openCalculateModal(): void {
    const totalAmount = this.enrollmentForm.get('totalAmount')?.value;
    if (!totalAmount || totalAmount <= 0) {
      this.toastService.showToast('warning', 'Debe ingresar un monto total válido antes de calcular las cuotas.');
      return;
    }
    this.showCalculateModal = true;
  }

  closeCalculateModal(): void {
    this.showCalculateModal = false;
  }

  confirmAutoCalculate(): void {
    this.applyAutoCalculation();
    this.closeCalculateModal();
  }

  private applyAutoCalculation(): void {
    const totalAmount = Number(this.enrollmentForm.get('totalAmount')?.value);

    if (totalAmount <= 0) {
      this.toastService.showToast('warning', 'El monto total debe ser mayor a 0.');
      return;
    }

    // 🔒 VALIDACIÓN: No permitir más de 4 cuotas en el cálculo automático
    if (this.autoCalculateInstallments > this.maxInstallments) {
      this.autoCalculateInstallments = this.maxInstallments;
      this.toastService.showToast('warning', `El número de cuotas ha sido ajustado al límite máximo: ${this.maxInstallments} cuotas.`);
    }

    // 🔄 CORRECCIÓN: Distribuir el MONTO TOTAL completo, no solo el restante
    // Limpiar cuotas actuales
    while (this.paymentPlans.length !== 0) {
      this.paymentPlans.removeAt(0);
    }

    const baseAmount = totalAmount / this.autoCalculateInstallments;
    const roundedAmount = this.roundToTwo(baseAmount);
    let accumulated = 0;

    for (let i = 0; i < this.autoCalculateInstallments; i++) {
      const isLastInstallment = i === this.autoCalculateInstallments - 1;
      let amountForThisInstallment: number;

      if (isLastInstallment) {
        amountForThisInstallment = this.roundToTwo(totalAmount - accumulated);
      } else {
        amountForThisInstallment = roundedAmount;
        accumulated += roundedAmount;
      }

      const dueDate = this.calculateNextDueDate(i + 1);

      const newPaymentPlan = this.fb.group({
        dueDate: [dueDate, Validators.required],
        installmentNumber: [i + 1, Validators.required],
        amountToPay: [amountForThisInstallment, [Validators.required, Validators.min(1)]],
        paymentStatus: ['PENDING'],
        payerType: ['PARTICIPANT'],
        isPunished: [false],
        generateLink: [i === 0], // 🆕 Generar link automático solo para primera cuota
        observations: [`Cuota ${i + 1} - Distribución automática`]
      });

      this.paymentPlans.push(newPaymentPlan);
    }

    // Actualizar número de cuotas en el formulario
    this.enrollmentForm.get('installments')?.setValue(this.autoCalculateInstallments);

    // 🔄 ACTUALIZAR TOTAL ASIGNADO DESPUÉS DE CREAR CUOTAS AUTOMÁTICAMENTE
    this.validateAssignedAmount();

    this.toastService.showToast('success', `${this.autoCalculateInstallments} cuotas generadas automáticamente.`);
  }

  private calculateNextDueDate(installmentNumber: number): Date {
    const today = new Date();
    const nextMonth = new Date(today);
    nextMonth.setMonth(today.getMonth() + installmentNumber);

    // Ajuste para meses con diferente cantidad de días
    if (nextMonth.getMonth() !== ((today.getMonth() + installmentNumber) % 12)) {
      nextMonth.setDate(0); // Último día del mes anterior
    }

    return nextMonth;
  }

  private roundToTwo(num: number): number {
    return Math.round(num * 100) / 100;
  }

  // 🔁 Reactividad de selección de tipo de inscripción
  setupEnrollmentTypeListener(): void {
    this.enrollmentForm.get('enrollmentType')?.valueChanges.subscribe((enrollmentType) => {
      this.selectedEnrollmentType = enrollmentType;

      // Establecer la moneda según el tipo de inscripción
      if (enrollmentType === 'national' || enrollmentType === 'business') {
        this.enrollmentForm.get('currencyId')?.setValue(1); // COP = 1
        this.businessIsInternational = false;
        this.businessCurrencyId = 1;
        this.businessCurrencyCode = 'COP';
      } else if (enrollmentType === 'international' || enrollmentType === 'business-international') {
        this.enrollmentForm.get('currencyId')?.setValue(2); // USD = 2
        this.businessIsInternational = enrollmentType === 'business-international';
        this.businessCurrencyId = 2;
        this.businessCurrencyCode = 'USD';
      }

      // Forzar detección de cambios para actualizar la UI
      this.cdr.detectChanges();

      // Filtrar proyectos según el tipo de inscripción seleccionado
      this.filterProjectsByEnrollmentType(enrollmentType);

      // Actualizar el monto de inscripción si ya hay un proyecto seleccionado
      const currentProjectId = this.enrollmentForm.get('projectId')?.value;
      if (currentProjectId && this.selectedProjectForCurrency) {
        if ((enrollmentType === 'international' || enrollmentType === 'business-international') && this.selectedProjectForCurrency.inscriptionAmountUSD) {
          this.enrollmentForm.get('inscriptionAmount')?.setValue(this.selectedProjectForCurrency.inscriptionAmountUSD);
          // También actualizar el monto de inscripción empresarial para tipo business-international
          if (enrollmentType === 'business-international') {
            this.businessInscriptionAmount = this.selectedProjectForCurrency.inscriptionAmountUSD;
          }
        } else {
          this.enrollmentForm.get('inscriptionAmount')?.setValue(this.selectedProjectForCurrency.inscriptionAmountCOP);
          if (enrollmentType === 'business') {
            this.businessInscriptionAmount = this.selectedProjectForCurrency.inscriptionAmountCOP;
          }
        }
      }

      // Si no hay proyecto seleccionado, limpiar y resetear
      if (!currentProjectId) {
        this.selectedProjectForCurrency = null;
        this.resetCurrencyInformation();
        this.resetBusinessCurrencyInformation();
      }
    });
  }

  // 🌍 Resetear información de moneda empresarial
  private resetBusinessCurrencyInformation(): void {
    this.businessExchangeRate = 0;
    this.businessLastExchangeRateUpdate = null;
  }

  // Filtrar proyectos según el tipo de inscripción
  filterProjectsByEnrollmentType(enrollmentType: string): void {
    if (!this.projects || this.projects.length === 0) return;

    // Aquí podrías filtrar los proyectos basado en el tipo de inscripción
    // Por ahora, todos los proyectos estarán disponibles
    // En el futuro podrías filtrar: this.filteredProjects = this.projects.filter(...)
  }

  // 🔁 Reactividad de selección de proyecto
  setupProjectSelectionListener(): void {
    this.enrollmentForm.get('projectId')?.valueChanges.subscribe(async (projectId) => {
      const selectedProject = this.projects.find((project: any) => project.data.id === Number(projectId));
      if (selectedProject) {
        this.selectedProjectForCurrency = selectedProject.data;

        // Para inscripciones internacionales, verificar si el proyecto maneja moneda extranjera
        if (this.selectedEnrollmentType === 'international') {
          // Determinar si es internacional basándose en el CurrencyId
          this.isInternationalProject = selectedProject.data.currencyId && selectedProject.data.currencyId > 1;

          // Si es proyecto internacional, cargar información de moneda
          if (this.isInternationalProject && selectedProject.data.currency) {
            await this.loadCurrencyInformation(selectedProject.data);
          }
        } else {
          this.isInternationalProject = false;
          this.resetCurrencyInformation();
        }

        // Establecer el monto de inscripción según el tipo
        if (this.selectedEnrollmentType === 'international' && selectedProject.data.inscriptionAmountUSD) {
          // Para inscripciones internacionales, mostrar el monto en moneda original
          this.enrollmentForm.get('inscriptionAmount')?.setValue(selectedProject.data.inscriptionAmountUSD);
        } else {
          // Para inscripciones nacionales, mostrar el monto en COP
          this.enrollmentForm.get('inscriptionAmount')?.setValue(selectedProject.data.inscriptionAmountCOP);
        }
      }
    });
  }

  // Cargar información de moneda para proyectos internacionales
  private async loadCurrencyInformation(project: any): Promise<void> {
    try {
      this.projectCurrency = project.currency;
      this.currentExchangeRate = project.currentExchangeRate || 0;
      this.lastExchangeRateUpdate = project.lastExchangeRateUpdate
        ? new Date(project.lastExchangeRateUpdate)
        : null;

      console.log('💱 Información de moneda cargada:', {
        currency: this.projectCurrency,
        rate: this.currentExchangeRate,
        lastUpdate: this.lastExchangeRateUpdate
      });
    } catch (error) {
      console.error('Error cargando información de moneda:', error);
      this.resetCurrencyInformation();
    }
  }

  // Resetear información de moneda
  private resetCurrencyInformation(): void {
    this.projectCurrency = null;
    this.currentExchangeRate = 0;
    this.lastExchangeRateUpdate = null;
  }

  // Formatear tasa de cambio para mostrar
  formatExchangeRate(): string {
    if (!this.projectCurrency || this.currentExchangeRate <= 0) {
      return 'No disponible';
    }
    return `1 ${this.projectCurrency.code} = ${this.currentExchangeRate.toFixed(2)} COP`;
  }

  // Calcular equivalente en moneda original
  calculateOriginalCurrencyEquivalent(copAmount: number): number {
    if (!this.isInternationalProject || this.currentExchangeRate <= 0) {
      return copAmount;
    }
    return copAmount / this.currentExchangeRate;
  }



  // Los campos de moneda internacional ahora se manejan automáticamente en el backend
  // usando ExchangeRateHistory - no es necesario calcular en el frontend

  // Obtener el símbolo de moneda según el tipo de inscripción
  getCurrentCurrencySymbol(): string {
    if (this.selectedEnrollmentType === 'international') {
      // Si hay proyecto con moneda específica, usarla; sino USD por defecto
      return this.projectCurrency?.symbol || 'USD$';
    }
    if (this.selectedEnrollmentType === 'business-international') {
      return this.getBusinessCurrencySymbol();
    }
    return '$'; // COP
  }

  // Obtener el código de moneda según el tipo de inscripción
  getCurrentCurrencyCode(): string {
    if (this.selectedEnrollmentType === 'international') {
      // Si hay proyecto con moneda específica, usarla; sino USD por defecto
      return this.projectCurrency?.code || 'USD';
    }
    if (this.selectedEnrollmentType === 'business-international') {
      return this.businessCurrencyCode;
    }
    return 'COP';
  }

  // Helper: Verificar si es flujo empresarial (nacional o internacional)
  isBusinessFlow(): boolean {
    return this.selectedEnrollmentType === 'business' || this.selectedEnrollmentType === 'business-international';
  }

  // Helper: Verificar si es flujo individual (nacional o internacional)
  isIndividualFlow(): boolean {
    return this.selectedEnrollmentType === 'national' || this.selectedEnrollmentType === 'international';
  }

  // 🧠 Paso del wizard
  goToStep(step: number): void {
    this.step = step;
  }

  // 🔁 FormArray: Payment Plans
  get paymentPlans(): FormArray {
    return this.enrollmentForm.get('paymentPlans') as FormArray;
  }

  createPaymentPlanItem(installmentNumber: number = 1, dueDate?: Date): FormGroup {
    // Calcular fecha de vencimiento automáticamente
    const calculatedDueDate = dueDate || this.calculateNextDueDate(installmentNumber);

    return this.fb.group({
      dueDate: [calculatedDueDate, Validators.required],
      installmentNumber: [installmentNumber, Validators.required],
      amountToPay: ['', Validators.required],
      paymentStatus: ['PENDING', Validators.required],
      payerType: ['PARTICIPANT'], // 🔄 SIEMPRE PARTICIPANT para inscripciones individuales
      isPunished: [false],
      generateLink: [installmentNumber === 1], // 🆕 Generar link automático solo para primera cuota por defecto
      observations: ['']
      // Los campos de moneda internacional ahora se manejan automáticamente en el backend
    });
  }

  addPaymentPlan(): void {
    // 🔒 VALIDACIÓN: No permitir más de 4 cuotas
    if (this.paymentPlans.length >= this.maxInstallments) {
      this.toastService.showToast('warning', `No se pueden agregar más cuotas. Límite máximo: ${this.maxInstallments} cuotas.`);
      return;
    }

    const nextInstallment = this.paymentPlans.length + 1;
    const newItem = this.createPaymentPlanItem(nextInstallment);

    // Configurar listeners para actualización automática de montos
    newItem.get('amountToPay')?.valueChanges.subscribe((copAmount) => {
      this.validateAssignedAmount();
    });

    newItem.valueChanges.subscribe(() => this.validateAssignedAmount());
    this.paymentPlans.push(newItem);
    this.validateAssignedAmount();
  }

  async removePaymentPlan(index: number): Promise<void> {
    const plansArray = this.paymentPlans;
    if (index < 0 || index >= plansArray.length) return;

    const plan = plansArray.at(index);
    const installmentNumber = plan?.get('installmentNumber')?.value || (index + 1);

    const canRemove = await this.confirmationService.enrollment.removePaymentPlan(installmentNumber);

    if (canRemove) {
      plansArray.removeAt(index);
      this.updateInstallmentNumbers();
      this.validateAssignedAmount();
    }
  }

  updateInstallmentNumbers(): void {
    this.paymentPlans.controls.forEach((control, i) => {
      control.get('installmentNumber')?.setValue(i + 1);
    });
  }

  // 🔐 Lock Handling con SignalR
  private setupLockListeners(): void {
    this.lockSubscription = this._signalRService.enrollmentLocked$.subscribe((data) => {
      this.updateFormLockState(data);
    });

    this.formSubscription = merge(
      this.enrollmentForm.get('projectId')!.valueChanges,
      this.enrollmentForm.get('participantId')!.valueChanges
    ).subscribe(() => this.handleFormChanges());
  }

  private async handleFormChanges(): Promise<void> {
    const projectId = this.enrollmentForm.get('projectId')?.value?.toString();
    const participantId = this.enrollmentForm.get('participantId')?.value?.toString();

    if (projectId && participantId) {
      try {
        const isLocked = await this._signalRService.checkEnrollmentLock(projectId, participantId);
        const lockSuccess = !isLocked ? await this._signalRService.lockEnrollment(projectId, participantId) : false;
        this.isBlocked = isLocked || !lockSuccess;
        this.cdr.detectChanges();
      } catch (error) {
        console.error('Error managing lock:', error);
        this.isBlocked = true;
      }
    }
  }

  private updateFormLockState(lockData: any): void {
    const currentProject = this.enrollmentForm.get('projectId')?.value?.toString();
    const currentParticipant = this.enrollmentForm.get('participantId')?.value?.toString();

    if (
      lockData &&
      lockData.projectId === currentProject &&
      lockData.participantId === currentParticipant &&
      lockData.lockedBy !== this._signalRService.userConnectionId
    ) {
      this.isBlocked = true;
      this.cdr.detectChanges();
    }
  }

  private unlockCurrentEnrollment(): void {
    const projectId = this.enrollmentForm.get('projectId')?.value?.toString();
    const participantId = this.enrollmentForm.get('participantId')?.value?.toString();
    if (projectId && participantId) {
      this._signalRService.unlockEnrollment(projectId, participantId);
    }
  }

  // ✅ Guardar inscripción
  async onSubmit(): Promise<void> {
    // 🚨 Verificar si hay links de pago automático activos
    if (this.hasAutoPaymentLinksEnabled()) {
      this.showPaymentLinkWarningModal = true;
      this.pendingSubmitAfterWarning = true;
      return;
    }
    await this.handleFormSubmit();
  }

  /**
   * 🚨 Verifica si alguna cuota tiene habilitado el link de pago automático
   * Aplica tanto a inscripciones individuales como corporativas
   */
  hasAutoPaymentLinksEnabled(): boolean {
    // Verificar en inscripciones individuales
    if (this.selectedEnrollmentType === 'national' || this.selectedEnrollmentType === 'international') {
      const plans = this.paymentPlans.controls;
      return plans.some(plan => plan.get('generateLink')?.value === true);
    }

    // Verificar en inscripciones corporativas (solo planes de empresa tienen generateLink)
    if (this.selectedEnrollmentType === 'business' || this.selectedEnrollmentType === 'business-international') {
      return this.companyPaymentPlans.some(plan => plan.generateLink === true);
    }

    return false;
  }

  /**
   * 🚨 Confirma continuar con links de pago automático
   */
  confirmPaymentLinkWarning(): void {
    this.showPaymentLinkWarningModal = false;
    if (this.pendingSubmitAfterWarning) {
      this.pendingSubmitAfterWarning = false;
      this.handleFormSubmit();
    }
  }

  /**
   * 🚨 Cancela la acción y cierra el modal de advertencia
   */
  cancelPaymentLinkWarning(): void {
    this.showPaymentLinkWarningModal = false;
    this.pendingSubmitAfterWarning = false;
  }

  /**
   * 🚨 Cuenta cuántos links de pago automático están activos
   */
  countAutoPaymentLinks(): number {
    if (this.selectedEnrollmentType === 'national' || this.selectedEnrollmentType === 'international') {
      return this.paymentPlans.controls.filter(plan => plan.get('generateLink')?.value === true).length;
    }

    if (this.selectedEnrollmentType === 'business' || this.selectedEnrollmentType === 'business-international') {
      return this.companyPaymentPlans.filter(plan => plan.generateLink === true).length;
    }

    return 0;
  }

  private async submitIndividualEnrollment(): Promise<void> {
    this._loadingService.show();

    // Procesar planes de pago con campos de moneda internacional
    const processedPaymentPlans = this.enrollmentForm.value.paymentPlans.map((plan: any) => ({
      dueDate: plan.dueDate,
      installmentNumber: plan.installmentNumber,
      amountToPay: Number(plan.amountToPay),
      paymentStatus: plan.paymentStatus,
      payerType: plan.payerType,
      isPunished: plan.isPunished || false,
      generateLink: plan.generateLink || false, // 🆕 Flag para generar link automático
      observations: plan.observations || ''
      // Los campos de moneda internacional ahora se manejan automáticamente en el backend
    }));

    // 🔄 MAPEAR ENROLLMENT TYPE AL ENUM DEL BACKEND + REMOVER CAMPOS OBSOLETOS
    const newEnrollment = {
      ...this.enrollmentForm.value,
      projectId: Number(this.enrollmentForm.value.projectId),
      participantId: Number(this.enrollmentForm.value.participantId),
      installments: Number(processedPaymentPlans.length),
      currencyId: Number(this.enrollmentForm.value.currencyId),
      enrollmentType: 1, // 🔄 SIEMPRE Individual (1) para inscripciones individuales
      autoGeneratePaymentLinks: this.enrollmentForm.value.autoGeneratePaymentLinks ?? true, // 🆕 Cobro automático
      paymentPlans: processedPaymentPlans
    };

    if (this.enrollmentForm.valid) {
      this._enrollmentService.insert(newEnrollment).subscribe({
        next: (response: any) => {
          this._signalRService.unlockEnrollment(newEnrollment.projectId, newEnrollment.participantId);
          // 🆕 Redirigir a la visualización de la inscripción individual recién creada
          const newEnrollmentId = response?.id ?? response?.data?.id ?? null;
          this.handleSuccess(
            'Estudiante inscrito correctamente.',
            newEnrollmentId ? ['/dashboard/enrollments/view-v2', newEnrollmentId] : ['/enrollments']
          );
        },
        error: (error: any) => {
          const errorMessage = this.extractErrorMessage(error);
          this.handleError(errorMessage);
        }
      });
    } else {
      this._loadingService.hide();
    }
  }

  private handleSuccess(message: string, navigateTo: any[] = ['/enrollments']): void {
    this._loadingService.hide();
    this.toastService.showToast('success', message);
    this.router.navigate(navigateTo);
  }

  private handleError(message: string): void {
    this._loadingService.hide();
    this.toastService.showToast('error', message);
  }

  /**
   * Extrae el mensaje de error de forma resiliente, 
   * manejando tanto strings simples como objetos de error
   */
  private extractErrorMessage(error: any): string {
    const defaultMessage = 'Se produjo un error al procesar la solicitud.';

    if (!error) {
      return defaultMessage;
    }

    // Si es un string directo
    if (typeof error === 'string') {
      return error;
    }

    // Si tiene propiedad message (formato estándar del servicio actualizado)
    if (error.message && typeof error.message === 'string') {
      return error.message;
    }

    // Si tiene propiedad errorMessage (formato alternativo)
    if (error.errorMessage && typeof error.errorMessage === 'string') {
      return error.errorMessage;
    }

    // Si tiene propiedad error que es string
    if (error.error && typeof error.error === 'string') {
      return error.error;
    }

    // Si tiene propiedad error que es objeto con message
    if (error.error && error.error.message) {
      return error.error.message;
    }

    // Si tiene propiedad title (errores de validación ASP.NET)
    if (error.title && typeof error.title === 'string') {
      return error.title;
    }

    return defaultMessage;
  }

  // Configurar watchers para cambios de moneda
  private setupCurrencyWatchers(): void {
    // Cuando cambie el tipo de inscripción, actualizar todos los planes de pago
    this.enrollmentForm.get('enrollmentType')?.valueChanges.subscribe(() => {
      this.updateAllPaymentPlansCurrency();
    });
  }

  // Actualizar moneda en todos los planes de pago
  private updateAllPaymentPlansCurrency(): void {
    const currencyId = this.enrollmentForm.get('currencyId')?.value;

    // La conversión de monedas ahora se maneja automáticamente en el backend
    // usando ExchangeRateHistory, no es necesario actualizar campos en el frontend
  }

  // 🏢 ===== MÉTODOS PARA FLUJO EMPRESARIAL MEJORADO =====

  // Navegación de pasos empresariales
  goToBusinessStep(step: number): void {
    this.businessStep = step;
  }

  // Validación de paso 1 empresarial
  isBusinessStep1Valid(): boolean {
    // 🆕 Estado Inicial es obligatorio: bloquea "Siguiente" hasta seleccionarlo
    return !!(this.selectedCompanyId && this.selectedProjectId &&
      (this.businessStatus === 0 || this.businessStatus === 1));
  }

  // Validación de paso 2 empresarial (modalidad)
  isBusinessStep2Valid(): boolean {
    return !!(this.selectedCoverageModality);
  }

  // Validación de paso 3 empresarial (participantes)
  isBusinessStep3Valid(): boolean {
    return !!(
      this.businessParticipants.length > 0 &&
      this.businessInscriptionAmount > 0 &&
      this.businessParticipants.every(p => p.participantId && p.coverageType)
    );
  }

  // 🆕 Validación de paso 4 empresarial (planes de pago)
  isBusinessStep4Valid(): boolean {
    // Validar que siempre haya planes de pago para la empresa
    const hasCompanyPlans = this.companyPaymentPlans.length > 0;

    // Para modalidad parcial, también debe haber plan maestro
    const hasParticipantPlans = this.selectedCoverageModality === CoverageModality.Total ||
      this.masterPaymentPlans.length > 0;

    return hasCompanyPlans && hasParticipantPlans && this.isCompanyPlansBalanced();
  }

  // Validación de paso 5 empresarial (resumen)
  isBusinessStep5Valid(): boolean {
    return this.isBusinessStep1Valid() &&
      this.isBusinessStep2Valid() &&
      this.isBusinessStep3Valid() &&
      this.isBusinessStep4Valid();
  }

  // Método universal de validación por paso
  isBusinessStepValid(step: number): boolean {
    switch (step) {
      case 1: return this.isBusinessStep1Valid();
      case 2: return this.isBusinessStep2Valid();
      case 3: return this.isBusinessStep3Valid();
      case 4: return this.isBusinessStep4Valid();
      case 5: return this.isBusinessStep5Valid();
      default: return false;
    }
  }

  // Obtener proyecto seleccionado
  get selectedProject(): any {
    if (!this.selectedProjectId || !this.projects) return null;
    const project = this.projects.find((p: any) => p.value === this.selectedProjectId);
    return project?.data || null;
  }

  // Obtener monto de inscripción del proyecto
  getProjectInscriptionAmount(): string {
    if (!this.selectedProject) {
      return 'Seleccione un proyecto';
    }

    // Formatear el monto según la moneda del proyecto
    const amount = this.selectedProject.inscriptionAmountCOP || 0;
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  // Obtener nombre de la empresa seleccionada
  getSelectedCompanyName(): string {
    if (!this.selectedCompanyId || !this.companies) return '';
    const company = this.companies.find((c: any) => c.value === this.selectedCompanyId);
    return company?.label || '';
  }

  // Manejar cambio de tipo de cobertura
  onCoverageTypeChange(participant: any): void {
    switch (participant.coverageType) {
      case 'FULL':
        participant.coverageValue = 100;
        break;
      case 'NONE':
        participant.coverageValue = 0;
        break;
      case 'PERCENTAGE':
        participant.coverageValue = participant.coverageValue || 50;
        break;
      case 'FIXED':
        participant.coverageValue = participant.coverageValue || 0;
        break;
    }
  }

  // Obtener placeholder para campo de cobertura
  getCoveragePlaceholder(coverageType: string): string {
    switch (coverageType) {
      case 'PERCENTAGE':
        return 'Ej: 50 (%)';
      case 'FIXED':
        return 'Ej: 500000 (COP)';
      default:
        return '';
    }
  }

  // Calcular cobertura de la empresa (refactorizado - DRY Principle)
  getCompanyCoverage(participant: any): string {
    const distribution = this.calculateIndividualParticipantDistribution(participant);
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(distribution.companyAmount);
  }

  // Calcular cobertura del participante (refactorizado - DRY Principle)
  getParticipantCoverage(participant: any): string {
    const distribution = this.calculateIndividualParticipantDistribution(participant);
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(distribution.participantAmount);
  }

  // 🆕 ===== MÉTODOS PARA MODALIDADES DE COBERTURA A/B =====

  /**
   * Calcular distribución de costos para un participante específico (Principio SRP)
   * @param participant Datos del participante
   * @returns Distribución de costos {companyAmount, participantAmount}
   */
  private calculateIndividualParticipantDistribution(participant: any): { companyAmount: number, participantAmount: number } {
    if (this.businessInscriptionAmount <= 0) {
      return { companyAmount: 0, participantAmount: 0 };
    }

    const totalAmount = this.businessInscriptionAmount;
    let companyAmount = 0;

    switch (participant.coverageType) {
      case 'FULL':
        companyAmount = totalAmount;
        break;
      case 'NONE':
        companyAmount = 0;
        break;
      case 'PERCENTAGE':
        const percentage = participant.coverageValue || 0;
        companyAmount = Math.round(totalAmount * (percentage / 100));
        break;
      case 'FIXED':
        companyAmount = Math.min(participant.coverageValue || 0, totalAmount);
        break;
      default:
        companyAmount = 0;
    }

    const participantAmount = totalAmount - companyAmount;
    return { companyAmount, participantAmount };
  }

  /** Cobertura empresa esperada = Σ companyAmount(p) sobre los participantes. */
  getExpectedCompanyTotal(): number {
    return this.roundToTwo(
      this.businessParticipants.reduce(
        (s, p) => s + (this.calculateIndividualParticipantDistribution(p).companyAmount || 0), 0));
  }

  /** Σ cuotas empresa cargadas. */
  getCompanyPlansSum(): number {
    return this.roundToTwo(
      this.companyPaymentPlans.reduce((s: number, p: any) => s + (Number(p.amountToPay) || 0), 0));
  }

  /** True si las cuotas empresa cuadran con la cobertura esperada (±0.01). */
  isCompanyPlansBalanced(): boolean {
    return Math.abs(this.getCompanyPlansSum() - this.getExpectedCompanyTotal()) <= 0.01;
  }

  /**
   * Manejar cambio de modalidad de cobertura
   */
  onCoverageModalityChange(modality: CoverageModality): void {
    this.selectedCoverageModality = modality;

    // Si es modalidad Total (100%), configurar todos los participantes automáticamente
    if (modality === CoverageModality.Total) {
      this.businessParticipants.forEach(participant => {
        participant.coverageType = CoverageType.FULL;
        participant.coverageValue = 100;
      });
    }

    // Recalcular totales
    this.calculateConsolidatedTotals();
  }

  /**
   * Calcular totales consolidados para mostrar en UI (Refactorizado - SRP + Cálculo Correcto)
   */
  calculateConsolidatedTotals(): void {
    if (this.businessParticipants.length === 0 || this.businessInscriptionAmount <= 0) {
      this.resetConsolidatedTotals();
      return;
    }

    // Total de la inscripción = monto base × número de participantes
    this.totalEnrollmentAmount = this.businessInscriptionAmount * this.businessParticipants.length;

    // Calcular totales usando el método auxiliar para cada participante
    let totalCompanyAmount = 0;
    let totalParticipantAmount = 0;

    this.businessParticipants.forEach(participant => {
      const distribution = this.calculateIndividualParticipantDistribution(participant);
      totalCompanyAmount += distribution.companyAmount;
      totalParticipantAmount += distribution.participantAmount;
    });

    this.totalCompanyAmount = totalCompanyAmount;
    this.totalParticipantAmount = totalParticipantAmount;

    // Validación de integridad: total empresa + total participantes = total inscripción
    const calculatedTotal = this.totalCompanyAmount + this.totalParticipantAmount;
    if (Math.abs(calculatedTotal - this.totalEnrollmentAmount) > 1) {
      console.warn('⚠️ Discrepancia en cálculo de totales:', {
        totalEnrollment: this.totalEnrollmentAmount,
        totalCompany: this.totalCompanyAmount,
        totalParticipants: this.totalParticipantAmount,
        calculatedTotal: calculatedTotal,
        difference: calculatedTotal - this.totalEnrollmentAmount
      });
    }
  }

  /**
   * Resetear totales consolidados (Principio SRP)
   */
  private resetConsolidatedTotals(): void {
    this.totalEnrollmentAmount = 0;
    this.totalCompanyAmount = 0;
    this.totalParticipantAmount = 0;
  }

  /**
   * Formatear moneda para mostrar en UI
   */
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  /**
   * Obtener descripción de la modalidad seleccionada
   */
  getCoverageModalityDescription(): string {
    switch (this.selectedCoverageModality) {
      case CoverageModality.Total:
        return '🟢 Modalidad A: La empresa paga el 100% de todas las inscripciones. Se generará 1 solo conjunto de links de pago para la empresa.';
      case CoverageModality.Partial:
        return '🟡 Modalidad B: Cobertura parcial. La empresa paga un porcentaje y los participantes el resto. Se generarán links separados para empresa y cada participante.';
      default:
        return '';
    }
  }

  /**
   * Validar datos antes de envío según modalidad
   */
  validateCoverageModalityData(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validaciones comunes
    if (!this.selectedCompanyId) {
      errors.push('Debe seleccionar una empresa');
    }
    if (!this.selectedProjectId) {
      errors.push('Debe seleccionar un proyecto');
    }
    if (this.businessParticipants.length === 0) {
      errors.push('Debe agregar al menos un participante');
    }
    if (this.businessInscriptionAmount <= 0) {
      errors.push('Debe especificar el monto de inscripción');
    }

    // Validaciones específicas por modalidad
    if (this.selectedCoverageModality === CoverageModality.Partial) {
      // Para modalidad parcial, verificar que al menos un participante tenga configuración válida
      const hasValidParticipant = this.businessParticipants.some(p =>
        p.participantId && p.coverageType &&
        (p.coverageType === CoverageType.FULL || p.coverageType === CoverageType.NONE ||
          (p.coverageValue !== null && p.coverageValue >= 0))
      );

      if (!hasValidParticipant) {
        errors.push('Debe configurar correctamente la cobertura para al menos un participante');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Enviar inscripción empresarial con modalidades A/B
   */
  async submitBusinessEnrollment(): Promise<void> {
    const validation = this.validateCoverageModalityData();

    if (!validation.isValid) {
      validation.errors.forEach(error => {
        this.toastService.showError(error);
      });
      return;
    }

    this._loadingService.show();

    try {
      // Construir request según modalidad
      const request: CorporateEnrollmentRequest = {
        companyId: this.selectedCompanyId!,
        projectId: this.selectedProjectId!,
        coverageModality: this.selectedCoverageModality,
        inscriptionAmount: this.businessInscriptionAmount, // 🆕 Usar el monto editable
        defaultCompanyCoveragePercent: this.selectedCoverageModality === CoverageModality.Total ? 100 : 50,
        isFixedCoverage: false,
        startDate: new Date(),
        endDate: undefined,
        terms: this.businessObservations,
        observations: this.businessObservations,
        participants: this.businessParticipants.map(p => ({
          participantId: p.participantId!,
          coverageType: p.coverageType,
          companyCoveragePercent: p.coverageType === CoverageType.PERCENTAGE ? (p.coverageValue || undefined) : undefined,
          companyCoverageAmount: p.coverageType === CoverageType.FIXED ? (p.coverageValue || undefined) : undefined
        })),
        companyPaymentPlans: this.companyPaymentPlans, // 🆕 Usar los planes específicos
        masterPaymentPlans: this.masterPaymentPlans, // 🆕 Usar los planes específicos
        // 🌍 Campos para inscripciones corporativas internacionales
        currencyId: this.businessIsInternational ? this.businessCurrencyId ?? undefined : undefined,
        isInternational: this.businessIsInternational,
        // 📊 Estado inicial de la inscripción (propiedad del wizard corporativo)
        status: this.businessStatus,
        // 🆕 Cobro automático vía email (propiedad del wizard corporativo)
        autoGeneratePaymentLinks: this.businessAutoGeneratePaymentLinks
      };

      console.log('🏢 Enviando inscripción corporativa con modalidad:', this.selectedCoverageModality,
        this.businessIsInternational ? '🌍 (Internacional)' : '🇨🇴 (Nacional)', request);

      const response = await this._corporateEnrollmentService.createCorporateEnrollment(request).toPromise();

      if (response) {
        this.toastService.showSuccess(response.message || 'Inscripción corporativa creada exitosamente');

        // Mostrar resumen detallado
        const summary = `
          ✅ Acuerdo #${response.agreementId} creado
          📊 Modalidad: ${response.coverageModality === CoverageModality.Total ? 'Total (100%)' : 'Parcial'}
          👥 Inscripciones: ${response.createdEnrollments}
          🔗 Links empresa: ${response.paymentLinksInfo.companyPaymentLinks}
          👤 Links participantes: ${response.paymentLinksInfo.participantPaymentLinks}
        `;

        console.log('✅ Respuesta exitosa:', summary, response);

        // 🆕 Redirigir a la visualización de la inscripción corporativa (acuerdo) recién creada
        setTimeout(() => {
          this.router.navigate(
            response.agreementId
              ? ['/dashboard/enrollments/corporate/view-v2', response.agreementId]
              : ['/enrollments']
          );
        }, 2000);
      }

    } catch (error: any) {
      console.error('❌ Error enviando inscripción corporativa:', error);
      const errorMessage = this.extractErrorMessage(error);
      this.toastService.showError(errorMessage);
    } finally {
      this._loadingService.hide();
    }
  }

  /**
   * Actualizar cálculos cuando cambian los participantes
   */
  onParticipantChange(): void {
    this.calculateConsolidatedTotals();
  }

  // 🆕 ===== MÉTODOS PARA MANEJO DE PLANES DE PAGO =====

  /**
   * Agregar nueva cuota al plan de pagos de la empresa
   */
  addCompanyPaymentPlan(): void {
    // 🔒 VALIDACIÓN: No permitir más de 4 cuotas para la empresa
    if (this.companyPaymentPlans.length >= this.maxInstallments) {
      this.toastService.showToast('warning', `No se pueden agregar más cuotas para la empresa. Límite máximo: ${this.maxInstallments} cuotas.`);
      return;
    }

    const nextInstallment = this.companyPaymentPlans.length + 1;
    const nextDate = this.calculateNextDueDate(nextInstallment);

    this.companyPaymentPlans.push({
      dueDate: nextDate,
      amountToPay: 0,
      installmentNumber: nextInstallment,
      observations: '',
      generateLink: nextInstallment === 1 // por defecto, generar link para la primera cuota
    });
  }

  /**
   * Remover cuota del plan de pagos de la empresa
   */
  async removeCompanyPaymentPlan(index: number): Promise<void> {
    if (index < 0 || index >= this.companyPaymentPlans.length) return;

    const plan = this.companyPaymentPlans[index];
    const planDescription = `Cuota ${plan.installmentNumber} - ${this.formatCurrency(plan.amountToPay)}`;

    const canRemove = await this.confirmDelete('cuota empresarial', planDescription);

    if (canRemove) {
      this.companyPaymentPlans.splice(index, 1);
      // Renumerar las cuotas
      this.companyPaymentPlans.forEach((plan, i) => {
        plan.installmentNumber = i + 1;
      });
    }
  }

  /**
   * Agregar nueva cuota al plan maestro de participantes
   */
  addMasterPaymentPlan(): void {
    // 🔒 VALIDACIÓN: No permitir más de 4 cuotas en el plan maestro
    if (this.masterPaymentPlans.length >= this.maxInstallments) {
      this.toastService.showToast('warning', `No se pueden agregar más cuotas al plan maestro. Límite máximo: ${this.maxInstallments} cuotas.`);
      return;
    }

    const nextInstallment = this.masterPaymentPlans.length + 1;
    const nextDate = this.calculateNextDueDate(nextInstallment);

    this.masterPaymentPlans.push({
      dueDate: nextDate,
      amountToPay: 0,
      installmentNumber: nextInstallment,
      observations: ''
    });
  }

  /**
   * Remover cuota del plan maestro de participantes
   */
  async removeMasterPaymentPlan(index: number): Promise<void> {
    if (index < 0 || index >= this.masterPaymentPlans.length) return;

    const plan = this.masterPaymentPlans[index];
    const planDescription = `Cuota maestro ${plan.installmentNumber} - ${this.formatCurrency(plan.amountToPay)}`;

    const canRemove = await this.confirmDelete('cuota del plan maestro', planDescription);

    if (canRemove) {
      this.masterPaymentPlans.splice(index, 1);
      // Renumerar las cuotas
      this.masterPaymentPlans.forEach((plan, i) => {
        plan.installmentNumber = i + 1;
      });
    }
  }

  // ❌ REMOVIDA: Función duplicada - ya existe en línea 357

  /**
   * Distribuir monto total entre las cuotas de la empresa proporcionalmente
   */
  distributeCompanyAmount(): void {
    if (this.companyPaymentPlans.length === 0 || this.totalCompanyAmount === 0) return;

    // 🔒 VALIDACIÓN: Verificar que no excedan el límite de cuotas
    if (this.companyPaymentPlans.length > this.maxInstallments) {
      this.toastService.showToast('warning', `No se puede distribuir en más de ${this.maxInstallments} cuotas.`);
      return;
    }

    const count = this.companyPaymentPlans.length;
    const total = this.totalCompanyAmount;
    const per = this.roundToTwo(total / count);
    let acc = 0;

    this.companyPaymentPlans.forEach((plan, index) => {
      const isLast = index === count - 1;
      plan.amountToPay = isLast ? this.roundToTwo(total - acc) : per;
      if (!isLast) acc = this.roundToTwo(acc + per);
    });
  }

  /**
   * Distribuir monto de participantes entre las cuotas del plan maestro
   * CORRECCIÓN CRÍTICA: Ahora calcula el monto real que cada participante debe pagar individualmente
   */
  distributeParticipantAmount(): void {
    if (this.masterPaymentPlans.length === 0 || this.businessParticipants.length === 0) return;

    // 🔒 VALIDACIÓN: Verificar que no excedan el límite de cuotas
    if (this.masterPaymentPlans.length > this.maxInstallments) {
      this.toastService.showToast('warning', `No se puede distribuir el plan maestro en más de ${this.maxInstallments} cuotas.`);
      return;
    }

    // CORRECCIÓN: Calcular el monto total real que pagarán todos los participantes
    let totalParticipantsRealAmount = 0;
    this.businessParticipants.forEach(participant => {
      const distribution = this.calculateIndividualParticipantDistribution(participant);
      totalParticipantsRealAmount += distribution.participantAmount;
    });

    // Distribuir este monto real entre las cuotas del plan maestro
    // Nota: El plan maestro representa el PROMEDIO por participante,
    // pero cada participante tendrá su monto ajustado en el backend
    const averageParticipantAmount = totalParticipantsRealAmount / this.businessParticipants.length;
    const count = this.masterPaymentPlans.length;
    const total = this.roundToTwo(averageParticipantAmount);
    const per = this.roundToTwo(total / count);
    let acc = 0;

    this.masterPaymentPlans.forEach((plan, index) => {
      const isLast = index === count - 1;
      plan.amountToPay = isLast ? this.roundToTwo(total - acc) : per;
      if (!isLast) acc = this.roundToTwo(acc + per);
    });

    console.log('📊 Distribución corregida del plan maestro:', {
      totalParticipantsAmount: totalParticipantsRealAmount,
      averagePerParticipant: averageParticipantAmount,
      plansDistribution: this.masterPaymentPlans.map(p => p.amountToPay)
    });
  }

  /**
   * Inicializar el monto de inscripción con el valor del proyecto seleccionado
   * 🌍 Considera si es inscripción corporativa internacional
   */
  initializeInscriptionAmount(): void {
    if (this.selectedProject) {
      // Para inscripciones corporativas internacionales, usar monto en USD si está disponible
      if (this.businessIsInternational && this.selectedProject.inscriptionAmountUSD) {
        this.businessInscriptionAmount = this.selectedProject.inscriptionAmountUSD;
      } else {
        this.businessInscriptionAmount = this.selectedProject.inscriptionAmountCOP || 0;
      }
      this.calculateConsolidatedTotals();
    }
  }

  /**
   * 🌍 Cargar información de moneda para inscripciones corporativas internacionales
   */
  async loadBusinessCurrencyInformation(project: any): Promise<void> {
    try {
      if (project.currency) {
        this.businessCurrencyCode = project.currency.code || 'USD';
        this.businessCurrencyId = project.currencyId;
      }

      if (project.currentExchangeRate) {
        this.businessExchangeRate = project.currentExchangeRate;
      }

      if (project.lastExchangeRateUpdate) {
        this.businessLastExchangeRateUpdate = new Date(project.lastExchangeRateUpdate);
      }

      console.log('🌍 Información de moneda empresarial cargada:', {
        currencyCode: this.businessCurrencyCode,
        currencyId: this.businessCurrencyId,
        exchangeRate: this.businessExchangeRate,
        lastUpdate: this.businessLastExchangeRateUpdate
      });
    } catch (error) {
      console.error('Error cargando información de moneda empresarial:', error);
      this.resetBusinessCurrencyInformation();
    }
  }

  /**
   * 🌍 Formatear tasa de cambio empresarial
   */
  formatBusinessExchangeRate(): string {
    if (this.businessExchangeRate <= 0) {
      return 'No disponible';
    }
    return `1 ${this.businessCurrencyCode} = ${this.businessExchangeRate.toFixed(2)} COP`;
  }

  /**
   * 🌍 Calcular equivalente en COP para inscripciones corporativas internacionales
   */
  calculateBusinessCOPEquivalent(amount: number): number {
    if (!this.businessIsInternational || this.businessExchangeRate <= 0) {
      return amount;
    }
    return amount * this.businessExchangeRate;
  }

  /**
   * 🌍 Obtener símbolo de moneda para flujo empresarial
   */
  getBusinessCurrencySymbol(): string {
    if (this.businessIsInternational) {
      return this.businessCurrencyCode === 'USD' ? 'USD$' : `${this.businessCurrencyCode}$`;
    }
    return '$'; // COP
  }

  // 🎯 ===== MÉTODOS DE CONTROL DE DISTRIBUCIÓN DE PLANES DE PAGO =====

  /**
   * Obtener el total asignado en las cuotas de la empresa
   */
  getCompanyAssignedAmount(): number {
    return this.companyPaymentPlans.reduce((total, plan) => total + (plan.amountToPay || 0), 0);
  }

  /**
   * Obtener el monto restante por asignar para la empresa
   */
  getCompanyRemainingAmount(): number {
    return this.totalCompanyAmount - this.getCompanyAssignedAmount();
  }

  /**
   * Obtener el porcentaje de progreso del plan de empresa
   */
  getCompanyProgressPercentage(): number {
    if (this.totalCompanyAmount === 0) return 0;
    return Math.round((this.getCompanyAssignedAmount() / this.totalCompanyAmount) * 100);
  }

  /**
   * Obtener el monto total que debe pagar cada participante (promedio)
   */
  getParticipantTotalAmount(): number {
    if (this.businessParticipants.length === 0) return 0;
    return Math.round(this.totalParticipantAmount / this.businessParticipants.length);
  }

  /**
   * Obtener el total asignado en el plan maestro
   */
  getMasterAssignedAmount(): number {
    return this.masterPaymentPlans.reduce((total, plan) => total + (plan.amountToPay || 0), 0);
  }

  /**
   * Obtener el monto restante por asignar en el plan maestro
   */
  getMasterRemainingAmount(): number {
    return this.getParticipantTotalAmount() - this.getMasterAssignedAmount();
  }

  /**
   * Obtener el porcentaje de progreso del plan maestro
   */
  getMasterProgressPercentage(): number {
    const participantTotal = this.getParticipantTotalAmount();
    if (participantTotal === 0) return 0;
    return Math.round((this.getMasterAssignedAmount() / participantTotal) * 100);
  }

  /**
   * Exponer Math para uso en el template
   */
  Math = Math;

  // === MÉTODOS DE CANCELACIÓN Y NAVEGACIÓN ===

  /**
   * Maneja el botón cancelar con confirmación
   */
  async onCancel(): Promise<void> {
    await this.handleFormCancel('/dashboard/enrollments');
  }

  /**
   * Confirmación antes de cambiar tipo de inscripción
   */
  async confirmEnrollmentTypeChange(fromType: string, toType: string): Promise<boolean> {
    if (this.hasUnsavedChanges()) {
      return await this.confirmationService.enrollment.changeType(fromType, toType);
    }
    return true;
  }

  /**
   * Maneja la confirmación del modal
   */
  onConfirmationModalConfirmed(): void {
    this.onModalConfirmed();
  }

  /**
   * Maneja la cancelación del modal
   */
  onConfirmationModalCancelled(): void {
    this.onModalCancelled();
  }

  /**
   * Maneja el cierre del modal
   */
  onConfirmationModalClosed(): void {
    this.onModalClosed();
  }

  /**
   * Confirmación especial para inscripciones empresariales con montos altos
   */
  async confirmBusinessEnrollmentSubmit(): Promise<boolean> {
    if (this.selectedEnrollmentType === 'business' && this.totalEnrollmentAmount > 5000000) {
      return await this.confirmationService.enrollment.highValueBusinessEnrollment(
        this.totalEnrollmentAmount,
        this.businessParticipants.length
      );
    }
    return true;
  }

  /**
   * Confirmación especial para inscripciones internacionales
   */
  async confirmInternationalEnrollmentSubmit(): Promise<boolean> {
    if (this.selectedEnrollmentType === 'international' && this.currentExchangeRate > 0) {
      const copAmount = this.enrollmentForm.get('totalAmount')?.value * this.currentExchangeRate;
      return await this.confirmationService.enrollment.internationalEnrollmentWithExchangeRate(
        this.enrollmentForm.get('totalAmount')?.value,
        this.getCurrentCurrencyCode(),
        copAmount,
        this.currentExchangeRate
      );
    }
    return true;
  }
}
